"use client";

/**
 * Auth Guard Component
 * @apal/shared-auth
 *
 * Protects routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Redirects to not-authorized if user lacks portal access.
 */

import * as React from "react";
import type { AuthGuardProps, PortalType } from "../types";
import { useAuth } from "../context";

/**
 * Extended props with navigation callbacks
 */
export interface AuthGuardComponentProps extends AuthGuardProps {
  /** Callback when user needs to redirect to login */
  onUnauthenticated?: (portal: PortalType) => void;
  /** Callback when user lacks portal access */
  onUnauthorized?: (portal: PortalType) => void;
  /** Current portal context */
  currentPortal?: PortalType;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
}

/**
 * AuthGuard - Protects routes requiring authentication
 *
 * @example
 * ```tsx
 * // With navigation callbacks
 * <AuthGuard
 *   currentPortal="APAL"
 *   onUnauthenticated={(portal) => navigate(`/${portal.toLowerCase()}/login`)}
 *   onUnauthorized={() => navigate("/not-authorized")}
 * >
 *   <DashboardPage />
 * </AuthGuard>
 *
 * // With specific portal requirement
 * <AuthGuard requiredPortal="HUMANDA">
 *   <HumandaDashboard />
 * </AuthGuard>
 * ```
 */
export function AuthGuard({
  children,
  requiredPortal,
  currentPortal = "APAL",
  onUnauthenticated,
  onUnauthorized,
  loadingComponent,
}: AuthGuardComponentProps): React.JSX.Element | null {
  const { isAuthenticated, isLoading, hasPortalAccess } = useAuth();

  // Determine which portal to check access for
  const portalToCheck = requiredPortal ?? currentPortal;

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    // Not authenticated - call unauthenticated callback
    if (!isAuthenticated) {
      onUnauthenticated?.(portalToCheck);
      return;
    }

    // Authenticated but lacks portal access - call unauthorized callback
    if (!hasPortalAccess(portalToCheck)) {
      onUnauthorized?.(portalToCheck);
      return;
    }
  }, [
    isAuthenticated,
    isLoading,
    hasPortalAccess,
    portalToCheck,
    onUnauthenticated,
    onUnauthorized,
  ]);

  // Still loading
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return null;
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Lacks portal access
  if (!hasPortalAccess(portalToCheck)) {
    return null;
  }

  return <>{children}</>;
}
